import { UserManager } from 'oidc-client-ts';
import { z } from 'zod';

const ConfigJsonResponseBody = z.object({
  authority: z.string().url(),
  client_id: z.string(),
  redirect_uri: z.string().url(),
  response_type: z.string(),
  scope: z.string(),
  response_mode: z.enum(['query']).optional(),
});
type ConfigJsonResponseBody = z.infer<typeof ConfigJsonResponseBody>;

const LoginAttemptsData = z.object({
  attempts: z.number(),
  lastAttempt: z.date(),
});
type LoginAttemptsData = z.infer<typeof LoginAttemptsData>;

class LoginAttempts {
  private readonly storageKey = 'login_attempts';
  constructor(private readonly now = new Date()) {
  }

  private getData() {
    const data = localStorage.getItem(this.storageKey);
    if (data) {
      return LoginAttemptsData.parse(JSON.parse(data));
    }
    return { attempts: 0, lastAttempt: this.now };
  }

  increment() {
    const data = this.getData();
    const timeSinceLastAttempt = this.now.getTime() - new Date(data.lastAttempt).getTime();
    if (data.attempts >= 5 && timeSinceLastAttempt < 5 * 60 * 1000) {
      throw new Error('Too many login attempts. Please try again later.');
    }

    data.attempts += 1;
    data.lastAttempt = this.now;
    localStorage.setItem(this.storageKey, JSON.stringify(data));
  }

  reset() {
    localStorage.removeItem(this.storageKey);
  }
}

export class UserManagerWrapper {
  private promise: Promise<UserManager> | null = null;
  private readonly loginAttempts = new LoginAttempts();
  constructor() {
  }

  async getUserManager(): Promise<UserManager> {
    if (!this.promise) {
      this.promise = UserManagerWrapper.createUserManager();
    }
    const res = await this.promise;
    return res;
  }

  private hasQueryParams() {
    const url = new URL(window.location.href);
    return url.searchParams.has('code') && url.searchParams.has('state');
  }

  private validateErrCanBeHandled(err: unknown): boolean {
    if (!(err instanceof Error)) {
      throw new Error('Unexpected error type');
    }
    if (err.message.includes('No state in response')) {
      return true;
    }
    if (err.message.includes('invalid_grant')) {
      return true;
    }
    return false;
  }

  /** @ts-expect-error signin redirect will throw an error or redirect to a new page */
  async signinRedirect(): Promise<never> {
    const userManager = await this.getUserManager();
    await userManager.signinRedirect();
  }

  async getUser() {
    try {
      const userManager = await this.getUserManager();
      let user;
      if (this.hasQueryParams()) {
        user = await userManager.signinRedirectCallback();
      } else {
        user = await userManager.getUser();
      }
      this.loginAttempts.reset();
      return user;
    } catch (err) {
      if (this.validateErrCanBeHandled(err)) {
        this.loginAttempts.increment();
        return null;
      }
      throw err;
    }
  }

  private static async createUserManager(): Promise<UserManager> {
    const config = await UserManagerWrapper.loadConfig();
    return new UserManager({
      authority: config.authority,
      client_id: config.client_id,
      redirect_uri: config.redirect_uri,
      response_type: config.response_type,
      scope: config.scope,
      response_mode: config.response_mode,
    });
  }

  private static async loadConfig(): Promise<ConfigJsonResponseBody> {
    const response = await fetch('cognito.json');
    if (!response.ok) {
      throw new Error(`Failed to fetch cognito config: ${response.statusText}`);
    }
    const body = await response.json();
    return ConfigJsonResponseBody.parse(body);
  }
}

export const wrapper = new UserManagerWrapper();
